import { Wallet, TradingCall, CallPerformance, WalletPerformance } from '../types/TradingCall';

const MAX_EQUITY_POINTS = 300;

export class WalletService {
  private wallet: Wallet;

  constructor(initialBalance: number = 100000) {
    const start = Math.max(100, initialBalance);
    this.wallet = {
      id: 'main-wallet',
      name: 'Virtual Paper Portfolio',
      initialBalance: start,
      balance: start,
      totalPnL: 0,
      activeCalls: [],
      closedCalls: [],
      performance: this.initializePerformance(),
      equityHistory: [{ ts: new Date().toISOString(), equity: start }],
    };
  }

  /**
   * Add a new trading call to the wallet
   */
  addTradingCall(call: TradingCall): void {
    if (call.portfolioLayer == null) {
      call.portfolioLayer = 'platform';
    }
    if (call.portfolioLayer === 'platform' && call.platformSource == null) {
      call.platformSource = 'scanner';
    }
    const symKey = String(call.symbol ?? 'UNK').replace(/\s+/g, '_');
    if (!call.id || String(call.id).trim() === '') {
      call.id = `${symKey}_${Date.now()}`;
    }
    if (!call.timestamp || !(call.timestamp instanceof Date) || Number.isNaN(call.timestamp.getTime())) {
      call.timestamp = new Date();
    }
    call.status = 'ACTIVE';
    call.performance = {
      currentPrice: call.entryPrice,
      pnl: 0,
      pnlPercentage: 0,
      maxProfit: 0,
      maxLoss: 0,
      timeElapsed: 0,
      lastUpdate: new Date(),
    };

    this.wallet.activeCalls.push(call);
    this.updateWalletBalance();
  }

  private safePnL(call: TradingCall, currentPrice: number): number {
    const raw = this.calculatePnL(call, currentPrice);
    return Number.isFinite(raw) ? raw : 0;
  }

  /**
   * Update call performance with current market data
   */
  updateCallPerformance(callId: string, currentPrice: number): void {
    const call = this.wallet.activeCalls.find(c => c.id === callId);
    if (!call) return;

    const pnl = this.safePnL(call, currentPrice);
    const denom = call.entryPrice;
    const pnlPercentage = denom !== 0 && Number.isFinite(denom) ? (pnl / denom) * 100 : 0;
    const timeElapsed = Date.now() - call.timestamp.getTime();

    call.performance = {
      currentPrice,
      pnl,
      pnlPercentage,
      maxProfit: Math.max(call.performance?.maxProfit || 0, pnl),
      maxLoss: Math.min(call.performance?.maxLoss || 0, pnl),
      timeElapsed,
      lastUpdate: new Date()
    };

    // Check if target or stop loss hit
    this.checkCallStatus(call);
  }

  /**
   * Calculate P&L for a trading call
   */
  private calculatePnL(call: TradingCall, currentPrice: number): number {
    const quantity = call.quantity;
    
    switch (call.strategy.type) {
      case 'FUTURES_LONG':
        return (currentPrice - call.entryPrice) * quantity;
      case 'FUTURES_SHORT':
        return (call.entryPrice - currentPrice) * quantity;
      case 'NAKED_CALL':
      case 'NAKED_PUT':
      case 'STRADDLE':
      case 'STRANGLE':
      case 'VERTICAL_SPREAD':
      case 'COLLAR':
      case 'IRON_CONDOR':
      case 'BUTTERFLY':
      case 'CALENDAR_SPREAD':
        return (currentPrice - call.entryPrice) * quantity;
      case 'COMPLEX_SPREAD':
        return (currentPrice - call.entryPrice) * quantity;
      default:
        return 0;
    }
  }

  /**
   * Check if call should be closed based on target/stop loss
   */
  private checkCallStatus(call: TradingCall): void {
    if (!call.performance) return;

    const { pnl, currentPrice } = call.performance;

    // Check target hit
    if (call.direction === 'BUY' && currentPrice >= call.targetPrice) {
      this.closeCall(call.id, 'TARGET_HIT', currentPrice);
      return;
    }

    if (call.direction === 'SELL' && currentPrice <= call.targetPrice) {
      this.closeCall(call.id, 'TARGET_HIT', currentPrice);
      return;
    }

    // Check stop loss
    if (call.direction === 'BUY' && currentPrice <= call.stopLoss) {
      this.closeCall(call.id, 'STOP_LOSS', currentPrice);
      return;
    }

    if (call.direction === 'SELL' && currentPrice >= call.stopLoss) {
      this.closeCall(call.id, 'STOP_LOSS', currentPrice);
      return;
    }

    // Check expiration (for options)
    if (call.callType === 'OPTIONS') {
      const expiryDate = new Date(call.strategy.details.expiry || '');
      if (new Date() > expiryDate) {
        this.closeCall(call.id, 'EXPIRED', currentPrice);
        return;
      }
    }
  }

  /**
   * Close a trading call
   */
  closeCall(callId: string, status: TradingCall['status'], closePrice: number): void {
    const callIndex = this.wallet.activeCalls.findIndex(c => c.id === callId);
    if (callIndex === -1) return;

    const call = this.wallet.activeCalls[callIndex];
    call.status = status;
    
    // Update final performance
    if (call.performance) {
      call.performance.currentPrice = closePrice;
      const pnl = this.safePnL(call, closePrice);
      call.performance.pnl = pnl;
      const denom = call.entryPrice;
      call.performance.pnlPercentage = denom !== 0 && Number.isFinite(denom) ? (pnl / denom) * 100 : 0;
      call.performance.lastUpdate = new Date();
    }

    // Move to closed calls
    this.wallet.closedCalls.push(call);
    this.wallet.activeCalls.splice(callIndex, 1);

    // Update wallet balance and performance
    this.updateWalletBalance();
    this.updatePerformanceMetrics();
  }

  /**
   * Manually close a call
   */
  manualCloseCall(callId: string, closePrice: number): void {
    this.closeCall(callId, 'MANUAL_CLOSE', closePrice);
  }

  /**
   * Cancel a call
   */
  cancelCall(callId: string): void {
    this.closeCall(callId, 'CANCELLED', 0);
  }

  /**
   * Update wallet balance based on active and closed calls
   */
  private updateWalletBalance(): void {
    const activePnL = this.wallet.activeCalls.reduce((sum, call) => {
      return sum + (call.performance?.pnl || 0);
    }, 0);

    const closedPnL = this.wallet.closedCalls.reduce((sum, call) => {
      return sum + (call.performance?.pnl || 0);
    }, 0);

    this.wallet.totalPnL = activePnL + closedPnL;
    this.wallet.balance = this.wallet.initialBalance + this.wallet.totalPnL;
    this.recordEquitySnapshot();
  }

  /** Append current equity for virtual portfolio performance chart (deduped ~1/min) */
  recordEquitySnapshot(): void {
    const equity = this.wallet.balance;
    const ts = new Date().toISOString();
    const last = this.wallet.equityHistory[this.wallet.equityHistory.length - 1];
    if (last && Math.abs(last.equity - equity) < 0.01) {
      const lastMin = last.ts.slice(0, 16);
      const nowMin = ts.slice(0, 16);
      if (lastMin === nowMin) return;
    }
    this.wallet.equityHistory.push({ ts, equity });
    if (this.wallet.equityHistory.length > MAX_EQUITY_POINTS) {
      this.wallet.equityHistory = this.wallet.equityHistory.slice(-MAX_EQUITY_POINTS);
    }
  }

  setInitialBalance(amount: number): void {
    this.wallet.initialBalance = Math.max(100, Math.round(amount));
    this.updateWalletBalance();
  }

  getUnrealizedPnL(): number {
    return this.wallet.activeCalls.reduce((s, c) => s + (c.performance?.pnl || 0), 0);
  }

  getRealizedPnL(): number {
    return this.wallet.closedCalls.reduce((s, c) => s + (c.performance?.pnl || 0), 0);
  }

  getTotalReturnPercent(): number {
    if (this.wallet.initialBalance <= 0) return 0;
    return (this.wallet.balance - this.wallet.initialBalance) / this.wallet.initialBalance * 100;
  }

  /**
   * Update performance metrics
   */
  private updatePerformanceMetrics(): void {
    const closedCalls = this.wallet.closedCalls;
    if (closedCalls.length === 0) return;

    const totalTrades = closedCalls.length;
    const winningTrades = closedCalls.filter(call => (call.performance?.pnl || 0) > 0).length;
    const losingTrades = totalTrades - winningTrades;
    const winRate = (winningTrades / totalTrades) * 100;

    const profits = closedCalls
      .filter(call => (call.performance?.pnl || 0) > 0)
      .map(call => call.performance?.pnl || 0);
    
    const losses = closedCalls
      .filter(call => (call.performance?.pnl || 0) < 0)
      .map(call => Math.abs(call.performance?.pnl || 0));

    const averageWin = profits.length > 0 ? profits.reduce((sum, pnl) => sum + pnl, 0) / profits.length : 0;
    const averageLoss = losses.length > 0 ? losses.reduce((sum, loss) => sum + loss, 0) / losses.length : 0;
    
    const profitFactor = averageLoss > 0 ? averageWin / averageLoss : 0;

    // Calculate max drawdown
    const maxDrawdown = this.calculateMaxDrawdown(closedCalls);

    // Calculate Sharpe ratio (simplified)
    const returns = closedCalls.map(call => (call.performance?.pnlPercentage || 0) / 100);
    const avgReturn = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
    const variance = returns.reduce((sum, ret) => sum + Math.pow(ret - avgReturn, 2), 0) / returns.length;
    const sharpeRatio = variance > 0 ? avgReturn / Math.sqrt(variance) : 0;

    this.wallet.performance = {
      totalTrades,
      winningTrades,
      losingTrades,
      winRate,
      averageWin,
      averageLoss,
      profitFactor,
      maxDrawdown,
      sharpeRatio
    };
  }

  /**
   * Calculate maximum drawdown
   */
  private calculateMaxDrawdown(closedCalls: TradingCall[]): number {
    let peak = 0;
    let maxDrawdown = 0;
    let runningBalance = this.wallet.initialBalance;

    for (const call of closedCalls) {
      const pnl = call.performance?.pnl || 0;
      runningBalance += pnl;

      if (runningBalance > peak) {
        peak = runningBalance;
      }

      const drawdown = (peak - runningBalance) / peak * 100;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
      }
    }

    return maxDrawdown;
  }

  /**
   * Initialize performance metrics
   */
  private initializePerformance(): WalletPerformance {
    return {
      totalTrades: 0,
      winningTrades: 0,
      losingTrades: 0,
      winRate: 0,
      averageWin: 0,
      averageLoss: 0,
      profitFactor: 0,
      maxDrawdown: 0,
      sharpeRatio: 0
    };
  }

  /**
   * Get wallet data
   */
  getWallet(): Wallet {
    return this.wallet;
  }

  /**
   * Get active calls
   */
  getActiveCalls(): TradingCall[] {
    return this.wallet.activeCalls;
  }

  /**
   * Get closed calls
   */
  getClosedCalls(): TradingCall[] {
    return this.wallet.closedCalls;
  }

  /**
   * Get performance metrics
   */
  getPerformance(): WalletPerformance {
    return this.wallet.performance;
  }

  /**
   * Get call by ID
   */
  getCallById(callId: string): TradingCall | undefined {
    return [...this.wallet.activeCalls, ...this.wallet.closedCalls]
      .find(call => call.id === callId);
  }

  /**
   * Get calls by status
   */
  getCallsByStatus(status: TradingCall['status']): TradingCall[] {
    if (status === 'ACTIVE') {
      return this.wallet.activeCalls;
    } else {
      return this.wallet.closedCalls.filter(call => call.status === status);
    }
  }

  /**
   * Get calls by symbol
   */
  getCallsBySymbol(symbol: string): TradingCall[] {
    return [...this.wallet.activeCalls, ...this.wallet.closedCalls]
      .filter(call => call.symbol.includes(symbol) || call.underlying === symbol);
  }

  /**
   * Get calls by strategy type
   */
  getCallsByStrategy(strategyType: string): TradingCall[] {
    return [...this.wallet.activeCalls, ...this.wallet.closedCalls]
      .filter(call => call.strategy.type === strategyType);
  }

  /**
   * Export wallet data
   */
  exportWalletData(): string {
    return JSON.stringify(this.wallet, null, 2);
  }

  /**
   * Import wallet data
   */
  importWalletData(data: string): void {
    try {
      const importedWallet = JSON.parse(data);
      const bal = Number(importedWallet.balance);
      const pnl = Number(importedWallet.totalPnL);
      const inferredInitial =
        Number.isFinite(bal) && Number.isFinite(pnl) ? Math.max(100, bal - pnl) : 100000;
      const start = Math.max(100, Number(importedWallet.initialBalance) || inferredInitial);
      this.wallet = {
        ...importedWallet,
        initialBalance: start,
        name: importedWallet.name || 'Virtual Paper Portfolio',
        equityHistory: Array.isArray(importedWallet.equityHistory)
          ? importedWallet.equityHistory
          : [{ ts: new Date().toISOString(), equity: start + (importedWallet.totalPnL || 0) }],
        activeCalls: importedWallet.activeCalls.map((call: any) => ({
          ...call,
          timestamp: new Date(call.timestamp),
          performance: call.performance ? {
            ...call.performance,
            lastUpdate: new Date(call.performance.lastUpdate)
          } : undefined
        })),
        closedCalls: importedWallet.closedCalls.map((call: any) => ({
          ...call,
          timestamp: new Date(call.timestamp),
          performance: call.performance ? {
            ...call.performance,
            lastUpdate: new Date(call.performance.lastUpdate)
          } : undefined
        }))
      };
      this.updateWalletBalance();
    } catch (error) {
      console.error('Failed to import wallet data:', error);
    }
  }
} 